import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { MAX_VALUE_PT } from '@/lib/units/unit-conversion'
import {
  getCurrentPhotoFillCoveredSize,
  getMaxPhotoFillSize,
  resolveEffectivePhotoFillNaturalSize,
} from '@/lib/photos/photo-fill-resolution'
import { computePhotoFillOffsetAfterResize } from '@/lib/photos/photo-fill-resize-offset'
import {
  clampCropResizeToCoveredSize,
  clampOrientedResizeToMaxSize,
} from '@/lib/transforms/single-object-oriented-resize'
import type { ResizeHandle } from '../types/resize-handle'

type PanelGeometry = { x: number; y: number; width: number; height: number }

/**
 * Largest value (pt) the panel's W/H field can accept for a photo-filled shape before the photo
 * would need to scale past its max fill size (`getMaxPhotoFillSize`) in either dimension. When
 * `linked`, the other dimension scales proportionally too, so the field's own limit is whichever
 * is more restrictive: its own direct cap, or the cap implied by the other dimension's max
 * (projected through the shape's current aspect ratio).
 */
export function computeMaxPanelFieldValueForPhotoFill(params: {
  w0: number
  h0: number
  field: 'width' | 'height'
  linked: boolean
  maxWidth: number
  maxHeight: number
}): number {
  const { w0, h0, field, linked, maxWidth, maxHeight } = params

  if (!linked || !Number.isFinite(w0) || !Number.isFinite(h0) || w0 <= 0 || h0 <= 0) {
    return field === 'width' ? maxWidth : maxHeight
  }

  const maxWidthViaHeight = maxHeight * (w0 / h0)
  const maxHeightViaWidth = maxWidth * (h0 / w0)

  return field === 'width'
    ? Math.min(maxWidth, maxWidthViaHeight)
    : Math.min(maxHeight, maxHeightViaWidth)
}

/**
 * Upper bound (pt) for a W/H commit. A photo-filled shape has its own (usually tighter) max size
 * than the pasteboard — beyond it the photo would need to stretch past its 150 DPI print floor.
 * Fold it into the same range check so whichever bound is smaller is what's actually reported — a
 * value that exceeds both must show the photo's bound, not the generic pasteboard one.
 */
export function resolvePanelDimensionMaxPoints(params: {
  isMultiSelect: boolean
  activeObject: ShapeData | null
  field: 'width' | 'height'
  linked: boolean
}): number {
  const { isMultiSelect, activeObject, field, linked } = params
  let effectiveMaxPoints = MAX_VALUE_PT

  if (!isMultiSelect && activeObject?.photoFill) {
    const w0 = activeObject.width ?? 0
    const h0 = activeObject.height ?? 0
    const { naturalWidth, naturalHeight } = resolveEffectivePhotoFillNaturalSize(
      activeObject.photoFill
    )
    const maxSize = getMaxPhotoFillSize(naturalWidth, naturalHeight)

    if (maxSize && Number.isFinite(w0) && Number.isFinite(h0) && w0 > 0 && h0 > 0) {
      const photoMax = computeMaxPanelFieldValueForPhotoFill({
        w0,
        h0,
        field,
        linked,
        maxWidth: maxSize.maxWidth,
        maxHeight: maxSize.maxHeight,
      })

      effectiveMaxPoints = Math.min(effectiveMaxPoints, photoMax)
    }
  }

  return effectiveMaxPoints
}

/** Clamps a panel resize of a photo-filled shape to the photo's max fill size (and, unlinked, its covered crop). */
export function clampPanelResizeToPhotoFill(params: {
  shape: ShapeData
  photoFill: NonNullable<ShapeData['photoFill']>
  direction: ResizeHandle
  next: PanelGeometry
  linked: boolean
}): PanelGeometry {
  const { shape, photoFill, direction, next, linked } = params
  const { naturalWidth: effectiveNaturalWidth, naturalHeight: effectiveNaturalHeight } =
    resolveEffectivePhotoFillNaturalSize(photoFill)
  const maxSize = getMaxPhotoFillSize(effectiveNaturalWidth, effectiveNaturalHeight)

  let clamped = clampOrientedResizeToMaxSize({
    initial: shape,
    direction,
    next,
    maxWidth: maxSize?.maxWidth,
    maxHeight: maxSize?.maxHeight,
    aspectLocked: linked,
  })

  if (!linked) {
    const coveredSize = getCurrentPhotoFillCoveredSize(
      shape.width,
      shape.height,
      effectiveNaturalWidth,
      effectiveNaturalHeight,
      photoFill.scale
    )

    clamped = clampCropResizeToCoveredSize({
      initial: shape,
      direction,
      next: clamped,
      coveredWidth: coveredSize?.coveredWidth,
      coveredHeight: coveredSize?.coveredHeight,
    })
  }

  return clamped
}

/** Shape updates for a panel resize: the new geometry, plus a re-anchored photo fill offset when filled. */
export function photoFillUpdatesForPanelResize(params: {
  shape: ShapeData
  before: PanelGeometry
  geometry: PanelGeometry
  linked: boolean
}): Partial<ShapeData> {
  const { shape, before, geometry, linked } = params

  return shape.photoFill
    ? {
        ...geometry,
        photoFill: {
          ...shape.photoFill,
          ...computePhotoFillOffsetAfterResize({
            isCornerHandle: linked,
            initial: before,
            next: geometry,
            offsetX: shape.photoFill.offsetX,
            offsetY: shape.photoFill.offsetY,
            scale: shape.photoFill.scale,
            ...resolveEffectivePhotoFillNaturalSize(shape.photoFill),
          }),
        },
      }
    : geometry
}
